import { Box, Popover, PopoverBody, PopoverContent, PopoverTrigger, } from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import { BadgeGroupItemContext } from "./badge-group-item-context";
// Detect the index of an element inside container, at which
// Flexbox layout wraps elements and places them on the next line
function detectWrapIndex(container) {
    const isWrapped = container.offsetHeight < container.scrollHeight;
    if (!isWrapped) {
        return -1;
    }
    let index = 0;
    let previousChildY;
    for (const child of container.children) {
        if (!(child instanceof HTMLElement)) {
            continue;
        }
        const childY = child.offsetTop;
        // When the Y coordinate of a node is larger than a Y coordinate of
        // the previous node, it means this node is placed on the next line,
        // which happens when node doesn't fit anymore and Flexbox moves it below
        if (typeof previousChildY === "number" && childY > previousChildY) {
            return index;
        }
        previousChildY = childY;
        index++;
    }
    return -1;
}
export function BadgeGroup({ truncate = true, children, moreButtonProps, ...props }) {
    const container = useRef(null);
    // Index of the node at which Flexbox forced the element onto the next line
    const [wrappedAtIndex, setWrappedAtIndex] = useState(-1);
    // X coordinate of the "+4" button used to anchor that button to the last badge in the row
    const [moreButtonX, setMoreButtonX] = useState(undefined);
    useEffect(() => {
        const containerElement = container.current;
        if (!containerElement || !truncate) {
            return;
        }
        const recalculateLayout = () => {
            const wrappedAtIndex = detectWrapIndex(containerElement);
            setWrappedAtIndex(wrappedAtIndex);
            if (wrappedAtIndex === -1) {
                return;
            }
            const elementBeforeWrap = containerElement.children[wrappedAtIndex - 1];
            if (!elementBeforeWrap || !(elementBeforeWrap instanceof HTMLElement)) {
                return;
            }
            // Position "+4" button near the last badge and add a 4px gap
            const moreButtonX = elementBeforeWrap.offsetLeft + elementBeforeWrap.offsetWidth + 4;
            setMoreButtonX(moreButtonX);
        };
        // Recalculate layout when container is resized
        const resizeObserver = new ResizeObserver(recalculateLayout);
        resizeObserver.observe(containerElement);
        // Recalculate layout when badges are added, changed or removed
        const mutationObserver = new MutationObserver(recalculateLayout);
        mutationObserver.observe(containerElement, {
            childList: true,
            subtree: true,
        });
        return () => {
            resizeObserver.disconnect();
            mutationObserver.disconnect();
        };
    }, [truncate]);
    // Children nodes excluding `false`, `undefined`, `null`
    const childrenNodes = React.Children.toArray(children);
    return (React.createElement(Box, { position: "relative", display: "flex", gap: 1, ...props },
        React.createElement(Box, { ref: container, flex: "1", minWidth: 10, display: "flex", gap: 1, flexWrap: "wrap", 
            // 6 (24px) is the height of the badge
            height: truncate ? 6 : undefined, overflow: truncate ? "hidden" : undefined }, React.Children.map(children, (child, index) => {
            if (!React.isValidElement(child)) {
                return child;
            }
            return (React.createElement(BadgeGroupItemContext.Provider
            // eslint-disable-next-line react/jsx-no-constructed-context-values
            , { 
                // eslint-disable-next-line react/jsx-no-constructed-context-values
                value: { isHidden: index >= wrappedAtIndex } }, child));
        })),
        wrappedAtIndex >= 0 && typeof moreButtonX === "number" && (React.createElement(React.Fragment, null,
            React.createElement(Box, { flex: "none", visibility: "hidden", ...moreButtonProps },
                "+",
                childrenNodes.length - wrappedAtIndex),
            React.createElement(Popover, { isLazy: true },
                React.createElement(PopoverTrigger, null,
                    React.createElement(Box, { as: "button", type: "button", position: "absolute", top: 0, left: 0, transform: `translateX(${moreButtonX}px)`, _focusVisible: {
                            shadow: "outline",
                            outline: "none",
                        }, ...moreButtonProps },
                        "+",
                        childrenNodes.length - wrappedAtIndex)),
                React.createElement(PopoverContent, { width: "max-content", maxWidth: "lg" },
                    React.createElement(PopoverBody, { display: "flex", flexWrap: "wrap", gap: 1, py: 4 }, React.Children.toArray(children).slice(wrappedAtIndex))))))));
}
